import { useState } from 'react';
import { Search, Zap, Mail, Calendar, Users, Phone, FolderClock, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

type Integration = {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: React.ReactNode;
  url?: string;
};

const integrations: Integration[] = [
  {
    id: 'instantly-ai',
    name: 'Instantly.ai',
    description: 'Powerful email automation tools',
    category: 'Marketing',
    icon: <Mail className="w-6 h-6" />,
    url: '/integrations/instantly',
  },
  {
    id: 'calendly',
    name: 'Calendly',
    description: 'Scheduling automation platform',
    category: 'Productivity',
    icon: <Calendar className="w-6 h-6" />,
    url: '/integrations/calendly',
  },
  {
    id: 'ringba',
    name: 'Ringba',
    description: 'Call tracking and analytics',
    category: 'Business',
    icon: <Phone className="w-6 h-6" />,
    url: '/integrations/ringba',
  },
  {
    id: 'monday',
    name: 'Monday.com',
    description: 'Project management platform',
    category: 'Productivity',
    icon: <FolderClock className="w-6 h-6" />,
    url: '/integrations/monday',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Customer relationship management',
    category: 'Business',
    icon: <Users className="w-6 h-6" />,
    url: '/integrations/salesforce',
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Time management and scheduling',
    category: 'Productivity',
    icon: <Calendar className="w-6 h-6" />,
    url: '/integrations/googlecalendar',
  },
  {
    id: 'retool',
    name: 'Retool',
    description: 'Build internal tools faster',
    category: 'Productivity',
    icon: <Wrench className="w-6 h-6" />,
    url: '/integrations/retool',
  },
  {
    id: 'bland-ai',
    name: 'Bland.ai',
    description: 'AI-powered customer service',
    category: 'Business',
    icon: <Users className="w-6 h-6" />,
    url: '/integrations/bland',
  },
  {
    id: 'twilio',
    name: 'Twilio',
    description: 'Cloud communications platform',
    category: 'Business',
    icon: <Phone className="w-6 h-6" />,
    url: '/integrations/twilio',
  },
];

const Integrations = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredIntegrations = integrations.filter(
    (integration) =>
      integration.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (activeCategory === 'All' || integration.category === activeCategory),
  );

  const categories = ['All', ...new Set(integrations.map((i) => i.category))];

  return (
    <div className="">
      <header className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-900">Integrations Library</h1>
        <p className="text-gray-600">Discover and connect powerful tools to enhance your workflow.</p>
      </header>

      <div className="flex flex-col gap-4 mb-8 sm:flex-row">
        <div className="relative flex-grow">
          <Search className="absolute text-gray-400 transform -translate-y-1/2 left-2 top-1/2" />
          <Input
            type="text"
            placeholder="Search integrations..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button>
          Add New Integration <Zap className="w-4 h-4 ml-2" />
        </Button>
      </div>

      <Tabs defaultValue="All" className="mb-8">
        <TabsList>
          {categories.map((category) => (
            <TabsTrigger key={category} value={category} onClick={() => setActiveCategory(category)}>
              {category}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredIntegrations.map((integration) => (
          <Card key={integration.id}>
            <CardHeader>
              <div className="flex items-center gap-2">
                {integration.icon}
                <CardTitle>{integration.name}</CardTitle>
              </div>
              <CardDescription>{integration.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Badge>{integration.category}</Badge>
            </CardContent>
            {integration.url ? (
              <CardFooter className="justify-between">
                <Button variant="outline">Learn More</Button>
                <Link to={integration.url}>
                  <Button>Connect</Button>
                </Link>
              </CardFooter>
            ) : (
              <CardFooter className="justify-between">
                <Button variant="outline">Learn More</Button>
                <Button>Coming Soon</Button>
              </CardFooter>
            )}
          </Card>
        ))}
      </div>

      {filteredIntegrations.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-lg text-gray-500">No integrations found. Try adjusting your search.</p>
        </div>
      )}
    </div>
  );
};

export default Integrations;
